import { useEffect, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";

const compareVisibleLabels = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
const compareOfficeLocations = (a, b) => (
  String(a.market ?? "").localeCompare(String(b.market ?? ""), undefined, { sensitivity: "base" })
  || compareVisibleLabels(a, b)
);

export function PickerPopover({ children, testId, width = 280, maxHeight = 380, style = {} }) {
  return (
    <div
      role="listbox"
      data-testid={testId}
      style={{
        position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 1000,
        width, maxHeight, overflowY: "auto", padding: 8, background: T.white,
        border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: T.shadowDropdown,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function PickerOption({ children, selected = false, nested = false, onClick, testId }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
        border: 0, background: selected ? "var(--v2-blue-tint)" : hovered ? "var(--v2-tint)" : "transparent",
        color: selected ? "var(--v2-accent)" : "var(--v2-dark)", borderRadius: 7,
        padding: nested ? "6px 8px 6px 20px" : "6px 8px", textAlign: "left",
        fontSize: T.fsBase, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

export function OfficeLocationLabel({ office }) {
  const market = office?.market;
  const alias = office?.label;
  return (
    <span>
      {market}
      {market && alias ? " · " : ""}
      {alias && (
        <span style={market ? { color: "var(--v2-light)", fontSize: T.fsXs } : undefined}>
          {alias}
        </span>
      )}
    </span>
  );
}

export function LocationScopePicker({
  data,
  scopeType,
  scopeValue,
  onSelect,
  testIdPrefix = "attendance",
  includeGroups = true,
  selectedLabel: selectedLabelOverride,
  stableMarketIdentity = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const query = search.trim();
  const matches = (item) => matchesFoldedQuery(query, item.label, item.value, item.market, item.region);
  const marketSelectionValue = (market) => stableMarketIdentity
    ? (market.scopeValue ?? market.marketId ?? `${market.region}::${market.value}`)
    : market.value;
  const selectedLabel = selectedLabelOverride ?? (scopeType === "all"
    ? "All locations"
    : data?.scope?.label || scopeValue || "All locations");
  const regions = includeGroups ? (data?.scopes?.regions ?? []).filter(matches) : [];
  const markets = includeGroups ? (data?.scopes?.markets ?? []).filter(matches).sort(compareVisibleLabels) : [];
  const offices = (data?.scopes?.offices ?? []).filter(matches).sort(compareOfficeLocations);
  const isAttendance = testIdPrefix === "attendance";
  const pickerTestId = isAttendance ? "attendance-location-picker" : `${testIdPrefix}-location-picker`;
  const menuTestId = isAttendance ? "attendance-location-menu" : `${testIdPrefix}-location-menu`;
  const searchTestId = isAttendance ? "attendance-location-search" : `${testIdPrefix}-location-search`;
  const selectTestId = isAttendance ? "v2-util-trend-location-select" : `v2-util-trend-${testIdPrefix}-location-select`;
  const triggerTestId = isAttendance ? "filter-dropdown-util-trend-location" : `filter-dropdown-util-trend-${testIdPrefix}-location`;
  const menuMarkerTestId = isAttendance ? "filter-dropdown-util-trend-location-menu" : `filter-dropdown-util-trend-${testIdPrefix}-location-menu`;

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        wrapperRef.current?.querySelector("button")?.focus();
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const choose = (type, value) => {
    onSelect(type, value);
    setOpen(false);
    setSearch("");
  };
  return (
    <div ref={wrapperRef} data-testid={pickerTestId} style={{ position: "relative" }}>
      <div data-testid={selectTestId}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid={triggerTestId}
          data-dropdown-trigger
          onClick={() => setOpen((value) => !value)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${T.border}`, background: T.white, color: "var(--v2-dark)", borderRadius: 8, padding: "6px 10px", fontSize: T.fsBase, fontWeight: T.fwSemibold, cursor: "pointer" }}
        >
          {selectedLabel}<span aria-hidden="true" style={{ color: "var(--v2-mid)", position: "relative", top: -3.5 }}>⌄</span>
        </button>
        {open && (
          <PickerPopover testId={menuTestId}>
            <span data-testid={menuMarkerTestId} aria-hidden="true" style={{ display: "none" }} />
            <input
              autoFocus
              aria-label="Search locations"
              data-testid={searchTestId}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search regions, markets, offices"
              style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 9px", fontSize: T.fsBase, marginBottom: 4 }}
            />
            <div style={{ padding: "6px 8px 3px", fontSize: T.fsXs, color: T.mid, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".06em" }}>Scope</div>
            <PickerOption selected={scopeType === "all"} onClick={() => choose("all", null)}>All locations</PickerOption>
            {regions.length > 0 && <div style={{ padding: "8px 8px 3px", fontSize: T.fsXs, color: T.mid, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".06em" }}>Regions</div>}
            {regions.map((region) => (
              <PickerOption key={region.value} selected={scopeType === "region" && scopeValue === region.value} onClick={() => choose("region", region.value)}>
                {region.label}<span style={{ color: "var(--v2-light)", fontSize: T.fsXs }}>{region.officeCount} offices</span>
              </PickerOption>
            ))}
            {markets.length > 0 && <div style={{ padding: "8px 8px 3px", fontSize: T.fsXs, color: T.mid, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".06em" }}>Markets</div>}
            {markets.map((market) => (
              <PickerOption key={`${market.region}-${market.value}`} selected={scopeType === "market" && scopeValue === marketSelectionValue(market)} onClick={() => choose("market", marketSelectionValue(market))}>
                <span>
                  {market.label}
                  {market.region && <small style={{ display: "block", color: "var(--v2-light)", fontSize: T.fsXs }}>{market.region}</small>}
                </span>
                <span style={{ color: "var(--v2-light)", fontSize: T.fsXs }}>{market.officeCount}</span>
              </PickerOption>
            ))}
            {offices.length > 0 && <div style={{ padding: "8px 8px 3px", fontSize: T.fsXs, color: T.mid, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".06em" }}>Offices</div>}
            {offices.map((office) => (
              <PickerOption key={office.value} selected={scopeType === "office" && scopeValue === office.value} nested onClick={() => choose("office", office.value)}>
                <OfficeLocationLabel office={office} />
              </PickerOption>
            ))}
            {regions.length + markets.length + offices.length === 0 && <div style={{ padding: 10, color: "var(--v2-mid)", fontSize: T.fsBase }}>No matching locations.</div>}
          </PickerPopover>
        )}
      </div>
    </div>
  );
}